import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { HorseArt } from "@/components/game/HorseArt";
import { HorseSilhouette } from "@/components/game/HorseSilhouette";
import { ArrowLeft, BookOpen, Lock, Crown, Loader2, Gem } from "lucide-react";

const RARITY_STYLES = {
  Legendary: "bg-amber-100 text-amber-700 border-amber-300",
  Rare: "bg-purple-100 text-purple-700 border-purple-300",
  Uncommon: "bg-sky-100 text-sky-700 border-sky-300",
  Common: "bg-muted text-muted-foreground border-border",
};

const CoatCard = ({ c }) => (
  <div data-testid={`coat-${c.id}`} data-unlocked={c.unlocked}
    className={`rounded-2xl border p-2.5 text-center ${c.unlocked ? "border-border bg-card" : "border-dashed border-border/70 bg-muted/40"}`}>
    <div className="mx-auto grid h-20 w-20 place-items-center overflow-hidden rounded-xl"
      style={{ background: c.unlocked ? `${c.color_hex || "#8a8a8a"}1a` : "#e5e5e5" }}>
      {c.unlocked ? (
        <HorseArt src={c.art_image} color={c.color_hex || "#8a8a8a"} size={72} />
      ) : (
        <div className="relative grid place-items-center">
          <div className="opacity-40 grayscale"><HorseSilhouette color="#9aa0a6" size={64} /></div>
          <Lock size={16} className="absolute text-muted-foreground" />
        </div>
      )}
    </div>
    <p className={`mt-1.5 truncate text-xs font-bold ${c.unlocked ? "text-primary" : "text-muted-foreground"}`}>
      {c.unlocked ? c.display_name : "Undiscovered"}
    </p>
    <p className="truncate text-[10px] text-muted-foreground">
      {c.unlocked ? (c.markings || c.base_color) : "Pick this coat to reveal"}
    </p>
    {c.unlocked && (
      <div className="mt-1.5 flex flex-col items-center gap-0.5" data-testid={`coat-rarity-${c.id}`}>
        <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${RARITY_STYLES[c.rarity] || RARITY_STYLES.Common}`}>
          <Gem size={9} /> {c.rarity}
        </span>
        <span className="text-[9px] text-muted-foreground">
          {c.owners <= 1 ? "Only you own this" : `${c.owners} trainers own this`}
        </span>
      </div>
    )}
  </div>
);

const Section = ({ title, icon: Icon, collected, total, coats, testid, accent }) => (
  <div className="mt-5" data-testid={testid}>
    <div className="mb-2 flex items-center gap-2">
      <Icon size={16} className={accent} />
      <h3 className="font-display text-lg font-bold text-primary">{title}</h3>
      <span data-testid={`${testid}-count`} className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
        {collected}/{total} collected
      </span>
    </div>
    {coats.length === 0 ? (
      <p className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No coats in this collection yet.</p>
    ) : (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {coats.map((c) => <CoatCard key={c.id} c={c} />)}
      </div>
    )}
  </div>
);

export const CollectorsBook = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    gameApi.get("/game/collectors-book").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background" data-testid="collectors-book">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="book-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <BookOpen size={18} className="text-secondary" />
        <h2 className="font-display text-xl font-bold text-primary">Collectors Book</h2>
        {data && (
          <span data-testid="book-total" className="ml-auto rounded-full bg-secondary/15 px-3 py-1 text-sm font-bold text-secondary">
            {data.collected}/{data.total}
          </span>
        )}
      </div>
      <div className="mx-auto max-w-2xl px-4 pb-16 pt-2">
        <p className="mt-2 text-sm text-muted-foreground">
          Every coat you've trained a mustang in. Pick a horse for a makeover to permanently reveal its coat here. Only you can see your book.
        </p>
        {loading ? (
          <p className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="book-loading"><Loader2 className="animate-spin" size={16} /> Opening your book…</p>
        ) : !data ? (
          <p className="mt-10 text-center text-sm text-muted-foreground" data-testid="book-error">Couldn't load your Collectors Book.</p>
        ) : (
          <>
            <Section title="Base Coats" icon={BookOpen} accent="text-secondary" testid="book-base"
              collected={data.base_collected} total={data.base_total} coats={data.base} />
            {(data.premium_groups && data.premium_groups.length > 0
              ? data.premium_groups
              : [{ key: "premium", label: "Premium Coats", coats: data.premium, collected: data.premium_collected, total: data.premium_total }]
            ).map((g) => (
              <Section key={g.key} title={g.label} icon={Crown} accent="text-amber-500" testid={`book-${g.key}`}
                collected={g.collected} total={g.total} coats={g.coats} />
            ))}
            {(data.level_rewards && data.level_rewards.length > 0) && (
              <div className="mt-5" data-testid="book-level-rewards">
                <div className="mb-2 flex items-center gap-2">
                  <Gem size={16} className="text-emerald-600" />
                  <h3 className="font-display text-lg font-bold text-primary">Piceance–East Douglas Level Rewards</h3>
                  <span data-testid="book-level-rewards-count" className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
                    {data.level_rewards_collected}/{data.level_rewards_total} unlocked
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {data.level_rewards.map((c) => (
                    <div key={c.id} data-testid={`level-reward-${c.required_level}`} data-unlocked={c.unlocked} data-discovered={c.discovered}
                      className={`rounded-2xl border p-2.5 text-center ${c.discovered ? "border-emerald-300 bg-card" : "border-dashed border-border/70 bg-muted/40"}`}>
                      <div className="mx-auto grid h-20 w-20 place-items-center overflow-hidden rounded-xl bg-muted">
                        {c.discovered && c.art_image ? (
                          <HorseArt src={c.art_image} color="#5a3e2b" size={72} />
                        ) : (
                          <div className="relative grid place-items-center">
                            <div className="opacity-40 grayscale"><HorseSilhouette color="#9aa0a6" size={64} /></div>
                            {!c.unlocked && <Lock size={16} className="absolute text-muted-foreground" />}
                          </div>
                        )}
                      </div>
                      <p className={`mt-1.5 truncate text-xs font-bold ${c.discovered ? "text-primary" : "text-muted-foreground"}`}>
                        {c.discovered ? c.display_name : "Undiscovered"}
                      </p>
                      <p className="truncate text-[10px] text-muted-foreground">
                        {c.discovered
                          ? `Level ${c.required_level} reward`
                          : c.unlocked
                            ? (c.art_pending ? `Level ${c.required_level} · artwork coming soon` : `Level ${c.required_level} · pick to reveal`)
                            : `Level ${c.required_level} · ${c.levels_remaining} to go`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
